import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { FaUsers, FaCheckCircle, FaCarSide, FaExclamationTriangle, FaMapMarkerAlt } from 'react-icons/fa';
import { CustomButton10Co, CustomButton10Ca } from '../../Templates';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../hooks/ToastContext';
import { supabase } from '../../data/supabaseClient';
import Spinner from '../../common/Spinner';

const ReadyConfirmation = ({ 
  ride, 
  currentUser, 
  otherUser, 
  userRole, 
  onUpdate, 
  addSystemMessage, 
  createNotification 
}) => {
  const { t } = useTranslation('common');
  const { addToast } = useToast();
  
  const [isConfirming, setIsConfirming] = useState(false);
  const [showSafetyDialog, setShowSafetyDialog] = useState(false);
  const [safetyChecked, setSafetyChecked] = useState(false);
  const [showOtherNotHereDialog, setShowOtherNotHereDialog] = useState(false);
  const [showProofDialog, setShowProofDialog] = useState(false);
  const [shareLocation, setShareLocation] = useState(false);
  const [isReporting, setIsReporting] = useState(false);
  
  // Add timeout tracking
  const timeoutRef = useRef(null);
  const [timeoutStarted, setTimeoutStarted] = useState(false);

  // Check ready status
  const isDriverUser = currentUser.id === ride.driver_id;
  const hasUserConfirmed = isDriverUser ? !!ride.driver_ready_time : !!ride.passenger_ready_time;
  const hasOtherConfirmed = isDriverUser ? !!ride.passenger_ready_time : !!ride.driver_ready_time;
  const bothReady = hasUserConfirmed && hasOtherConfirmed;
  
  // Check if there's an active dispute
  const hasActiveDispute = ride.status === 'presence_disputed';
  const isBeingAccused = ride.other_not_here_report && 
    ride.other_not_here_report.reported_by !== currentUser.id &&
    !ride.other_not_here_report.accused_response;

  // Setup timeout for dispute resolution
  useEffect(() => {
    if (hasActiveDispute && isBeingAccused && !timeoutStarted) {
      setTimeoutStarted(true);
      
      // Set timeout for 10 minutes (600000 ms)
      timeoutRef.current = setTimeout(() => {
        handleTimeoutResponse();
      }, 600000); // 10 minutes
      
      // Cleanup on unmount
      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    }
  }, [hasActiveDispute, isBeingAccused, timeoutStarted]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Handle timeout - automatically admit not here if no response
  const handleTimeoutResponse = async () => {
    try {
      const updatedReport = {
        ...ride.other_not_here_report,
        accused_response: 'timeout_no_response',
        accused_response_at: new Date().toISOString(),
        dispute_resolution: 'reporter_vindicated_timeout'
      };

      // Use valid cancellation reason - mutual_cancellation for timeout cases
      const { error } = await supabase
        .from('rides')
        .update({
          status: 'cancelled',
          cancelled_at: new Date().toISOString(),
          cancellation_reason: 'mutual_cancellation', // Valid reason
          no_show_party: userRole.toLowerCase() === 'driver' ? 'driver' : 'passenger', // Valid party
          other_not_here_report: updatedReport,
          updated_at: new Date().toISOString()
        })
        .eq('id', ride.id);

      if (error) throw error;

      // Notify the reporting user
      const reporterUserId = ride.other_not_here_report.reported_by;
      
      await createNotification(
        reporterUserId,
        'dispute_resolved',
        `${userRole} did not respond within 10 minutes - You were correct`,
        ride.id
      );

      await addSystemMessage(
        `${userRole} did not respond within 10 minutes - Ride cancelled due to no-show`
      );

      addToast({
        type: 'warning',
        title: t('ride_actions.response_timeout'),
        description: t('ride_actions.response_timeout_description')
      });
      
    } catch (error) {
      console.error('Error handling timeout response:', error);
    }
  };

  // Get current location with better error handling
  const getCurrentLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation not supported by this browser'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            timestamp: new Date().toISOString()
          });
        },
        (error) => {
          let errorMessage = 'Failed to get location';
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = 'Location access denied by user';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = 'Location information unavailable';
              break;
            case error.TIMEOUT:
              errorMessage = 'Location request timed out';
              break;
          }
          reject(new Error(errorMessage));
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 60000
        }
      );
    });
  };

  // Handle ready confirmation
  const handleReadyConfirmation = async () => {
    setIsConfirming(true);
    
    try {
      // Prepare update data
      const updateData = {
        updated_at: new Date().toISOString()
      };

      if (isDriverUser) {
        updateData.driver_ready_time = new Date().toISOString();
      } else {
        updateData.passenger_ready_time = new Date().toISOString();
      }

      // Check if other user has already confirmed
      const otherReady = isDriverUser ? !!ride.passenger_ready_time : !!ride.driver_ready_time;
      
      if (otherReady) {
        // Both users are now ready - start the journey
        updateData.status = 'ready_to_start';
        updateData.both_ready_confirmed = true;
      } else {
        // Keep status as both_present, waiting for other user
        updateData.status = 'both_present';
      }

      const { error } = await supabase
        .from('rides')
        .update(updateData)
        .eq('id', ride.id);

      if (error) throw error;

      // Create notification for other user
      const otherUserId = currentUser.id === ride.driver_id ? 
        ride.passenger_id : ride.driver_id;
      
      await createNotification(
        otherUserId,
        otherReady ? 'both_ready' : 'user_ready',
        otherReady ? 
          'Both users are ready to start the journey!' :
          `${userRole} is ready to start`,
        ride.id
      );

      await addSystemMessage(
        otherReady ? 
          'Both users are ready - Journey can begin! 🚗' :
          `${userRole} is ready to start - Waiting for ${userRole === 'Driver' ? 'passenger' : 'driver'}`
      );

      addToast({
        type: 'success',
        title: otherReady ? t('ride_actions.ready_to_start_toast_title') : t('ride_actions.ready_confirmed'),
        description: otherReady ? 
          t('ride_actions.both_ready_begin_journey') :
          t('ride_actions.waiting_for_other_confirm', { role: userRole === 'Driver' ? t('ride_actions.passenger') : t('ride_actions.driver') })
      });

      setShowSafetyDialog(false);
      
    } catch (error) {
      console.error('Error confirming ready status:', error);
      addToast({
        type: 'error',
        title: t('errors.error'),
        description: t('ride_actions.failed_confirm_ready')
      });
    } finally {
      setIsConfirming(false);
    }
  };

  // Handle "Other not here" report and cancel
  const handleReportAndCancel = async () => {
    setIsReporting(true);
    
    try {
      let locationProof = null;
      
      // Get location if user opted to share
      if (shareLocation) {
        try {
          locationProof = await getCurrentLocation();
        } catch (error) {
          console.log('Could not get location:', error);
          addToast({
            type: 'warning',
            title: t('ride_actions.location_not_available'),
            description: t('ride_actions.proceeding_without_proof')
          });
        }
      }

      const disputeReport = {
        reported_by: currentUser.id,
        reported_user: isDriverUser ? ride.passenger_id : ride.driver_id,
        reported_at: new Date().toISOString(),
        reporter_role: userRole,
        meeting_time: ride.meeting_time,
        reason: 'other_not_present',
        reporter_location_proof: locationProof,
        accused_response: null,
        accused_location_proof: null,
        dispute_resolution: 'pending'
      };

      const { error } = await supabase
        .from('rides')
        .update({
          status: 'presence_disputed',
          other_not_here_report: disputeReport,
          updated_at: new Date().toISOString()
        })
        .eq('id', ride.id);

      if (error) throw error;

      // Notify the other user about the dispute
      const otherUserId = currentUser.id === ride.driver_id ? 
        ride.passenger_id : ride.driver_id;
      const otherRole = userRole === 'Driver' ? 'Passenger' : 'Driver';
      
      await createNotification(
        otherUserId,
        'presence_disputed',
        `${userRole} reported you as not present - You have 10 minutes to respond`,
        ride.id
      );

      await addSystemMessage(
        `${userRole} reported that ${otherRole.toLowerCase()} is not present at the meeting point - ${otherRole} has 10 minutes to respond`
      );

      addToast({
        type: 'warning',
        title: t('ride_actions.report_submitted'),
        description: t('ride_actions.other_notified_10_minutes', { role: otherRole })
      });

      setShowOtherNotHereDialog(false);
      
    } catch (error) {
      console.error('Error reporting and cancelling:', error);
      addToast({
        type: 'error',
        title: t('errors.error'),
        description: t('ride_actions.failed_submit_report')
      });
    } finally {
      setIsReporting(false);
    }
  };

  // Handle accused user admitting fault - FIXED
  const handleAdmitNotHere = async () => {
    setIsConfirming(true);
    
    try {
      // Clear timeout if it exists
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      
      const updatedReport = {
        ...ride.other_not_here_report,
        accused_response: 'admit_not_here',
        accused_response_at: new Date().toISOString(),
        dispute_resolution: 'reporter_vindicated'
      };

      // Use valid cancellation reason and no_show_party
      const noShowParty = userRole.toLowerCase() === 'driver' ? 'driver' : 'passenger';
      const cancellationReason = noShowParty === 'driver' ? 'driver_no_show' : 'passenger_no_show';

      const { error } = await supabase
        .from('rides')
        .update({
          status: 'cancelled',
          cancelled_at: new Date().toISOString(),
          cancellation_reason: cancellationReason, // Use valid reason
          no_show_party: noShowParty, // Use valid party
          other_not_here_report: updatedReport,
          updated_at: new Date().toISOString()
        })
        .eq('id', ride.id);

      if (error) throw error;

      // Notify the reporting user
      const reporterUserId = ride.other_not_here_report.reported_by;
      
      await createNotification(
        reporterUserId,
        'dispute_resolved',
        `${userRole} confirmed they were not present - You were correct`,
        ride.id
      );

      await addSystemMessage(
        `${userRole} confirmed they were not present - Ride cancelled`
      );

      addToast({
        type: 'info',
        title: t('ride_actions.response_recorded'),
        description: t('ride_actions.ride_cancelled')
      });
      
    } catch (error) {
      console.error('Error admitting fault:', error);
      addToast({
        type: 'error',
        title: t('errors.error'),
        description: t('ride_actions.failed_submit_response')
      });
    } finally {
      setIsConfirming(false);
    }
  };

  // Handle accused user providing proof - FIXED
  const handleProvideProof = async () => {
    setIsConfirming(true);
    
    try {
      // Clear timeout if it exists
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      
      let locationProof = null;
      
      try {
        locationProof = await getCurrentLocation();
        console.log('Location proof obtained:', locationProof);
      } catch (error) {
        console.error('Location error:', error);
        addToast({
          type: 'error',
          title: t('ride_actions.location_required'),
          description: t('ride_actions.location_proof_required')
        });
        setIsConfirming(false);
        return;
      }

      const updatedReport = {
        ...ride.other_not_here_report,
        accused_response: 'dispute_with_proof',
        accused_response_at: new Date().toISOString(),
        accused_location_proof: locationProof,
        dispute_resolution: 'mutual_dispute'
      };

      // Use valid cancellation reason for dispute with evidence
      const { error } = await supabase
        .from('rides')
        .update({
          status: 'cancelled',
          cancelled_at: new Date().toISOString(),
          cancellation_reason: 'other', // Use 'other' for disputed cases
          other_not_here_report: updatedReport,
          updated_at: new Date().toISOString()
        })
        .eq('id', ride.id);

      if (error) throw error;

      // Notify the reporting user
      const reporterUserId = ride.other_not_here_report.reported_by;
      
      await createNotification(
        reporterUserId,
        'dispute_resolved',
        `${userRole} provided location proof - Dispute recorded with evidence`,
        ride.id
      );

      await addSystemMessage(
        `${userRole} provided location proof disputing the claim - Both evidence recorded`
      );

      addToast({
        type: 'success',
        title: t('ride_actions.proof_submitted'),
        description: t('ride_actions.location_proof_recorded')
      });

      setShowProofDialog(false);
      
    } catch (error) {
      console.error('Error providing proof:', error);
      addToast({
        type: 'error',
        title: t('errors.error'),
        description: t('ride_actions.failed_submit_proof')
      });
    } finally {
      setIsConfirming(false);
    }
  };

  // Handle starting the journey
  const handleStartJourney = async () => {
    if (!safetyChecked) {
      addToast({
        type: 'warning',
        title: t('ride_actions.safety_check_required'),
        description: t('ride_actions.confirm_safety_check_before_starting')
      });
      return;
    }

    setIsConfirming(true);
    
    try {
      const { error } = await supabase
        .from('rides')
        .update({
          status: 'in_progress',
          ride_start_time: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', ride.id);

      if (error) throw error;

      // Create notification for other user
      const otherUserId = currentUser.id === ride.driver_id ? 
        ride.passenger_id : ride.driver_id;
      
      await createNotification(
        otherUserId,
        'journey_started',
        'Journey has started - safe travels!',
        ride.id
      );

      await addSystemMessage('Journey started - Have a safe trip! 🛣️');

      addToast({
        type: 'success',
        title: t('ride_actions.journey_started'),
        description: t('ride_actions.have_safe_trip')
      });

      setShowSafetyDialog(false);
      
    } catch (error) {
      console.error('Error starting journey:', error);
      addToast({
        type: 'error',
        title: t('errors.error'),
        description: t('ride_actions.failed_start_journey')
      });
    } finally {
      setIsConfirming(false);
    }
  };

  // Main render
  return (
    <div className="RAC-ready-confirmation-compact">
      <div className="RAC-ready-header-compact">
        <FaUsers size={12} />
        <span>{t('ride_actions.ready_to_start_title')}</span>
      </div>
      
      <div className="RAC-ready-content-compact">
        <div className="RAC-both-present-message-compact">
          <FaUsers size={16} color="var(--accent-color)" />
          <span>{t('ride_actions.both_users_present')}</span>
        </div>


        {ride.status === 'ready_to_start' && bothReady ? (
          // Both users are ready - show start journey button
          <div className="RAC-start-journey-section-compact">
            <div className="RAC-start-message-compact">
              <FaCarSide size={14} />
              <span>{t('ride_actions.ready_to_begin')}</span>
            </div>
            <CustomButton10Co
              onClick={() => setShowSafetyDialog(true)}
              label={t('ride_actions.start_journey')}
              disabled={isConfirming}
            >
              <FaCarSide />
            </CustomButton10Co>
          </div>
        ) : hasActiveDispute ? (
          // Handle dispute state
          isBeingAccused ? (
            // Show options for accused user - FIXED LOGIC
            <div className="RAC-accused-response-section">
              <div className="RAC-dispute-message">
                <FaExclamationTriangle size={16} color="#F59E0B" />
                <span>{t('ride_actions.other_claims_not_here', { role: userRole === 'Driver' ? t('ride_actions.passenger') : t('ride_actions.driver') })}</span>
              </div>
              <div className="RAC-dispute-subtitle">
                {t('ride_actions.respond_within_10_minutes')}
              </div>
              
              <div className="RAC-dispute-actions">
                <CustomButton10Ca
                  onClick={handleAdmitNotHere}
                  disabled={isConfirming}
                >
                  {isConfirming ? <Spinner size="small" showText={false} /> : t('ride_actions.yes_not_here')}
                </CustomButton10Ca>
                <CustomButton10Co
                  onClick={() => setShowProofDialog(true)}
                  disabled={isConfirming}
                >
                  {t('ride_actions.i_am_here_share_proof')}
                </CustomButton10Co>
              </div>
            </div>
          ) : (
            // Show waiting for response for reporting user
            <div className="RAC-dispute-waiting">
              <FaExclamationTriangle size={16} color="#F59E0B" />
              <span>{t('ride_actions.waiting_for_response_role', { role: userRole === 'Driver' ? t('ride_actions.passenger') : t('ride_actions.driver') })}</span>
              <div className="RAC-dispute-timeout">
                {t('ride_actions.they_have_10_minutes')}
              </div>
            </div>
          )
        ) : hasUserConfirmed ? (
          // User has confirmed, waiting for other - SHOW OPTION TO REPORT
          <div className="RAC-waiting-confirmation-compact">
            <span>
              {t('ride_actions.waiting_for_role', { role: userRole === 'Driver' ? t('ride_actions.passenger') : t('ride_actions.driver') })}
            </span>
            <div className="RAC-waiting-actions">
              <CustomButton10Ca
                onClick={() => setShowOtherNotHereDialog(true)}
                label={t('ride_actions.other_person_not_here')}
                disabled={isConfirming}
              >
                <FaExclamationTriangle />
              </CustomButton10Ca>
            </div>
          </div>
        ) : (
          // User hasn't confirmed yet - show ready button AND "other not here" option
          <div className="RAC-confirm-ready-section-compact">
            <CustomButton10Co
              onClick={handleReadyConfirmation}
              label={t('ride_actions.im_ready')}
              disabled={isConfirming}
            >
              {isConfirming ? <Spinner size="small" showText={false} /> : <FaCheckCircle />}
            </CustomButton10Co>
            
            <CustomButton10Ca
              onClick={() => setShowOtherNotHereDialog(true)}
              label={t('ride_actions.other_person_not_here')}
              disabled={isConfirming}
            >
              <FaExclamationTriangle />
            </CustomButton10Ca>
          </div>
        )}
      </div>

      {/* Safety Dialog */}
      {showSafetyDialog && (
        <SafetyCheckDialog
          onCancel={() => setShowSafetyDialog(false)}
          onConfirm={handleStartJourney}
          isConfirming={isConfirming}
          safetyChecked={safetyChecked}
          setSafetyChecked={setSafetyChecked}
          t={t}
        />
      )}

      {/* Other Not Here Dialog */}
      {showOtherNotHereDialog && (
        <OtherNotHereDialog
          onCancel={() => setShowOtherNotHereDialog(false)}
          onConfirm={handleReportAndCancel}
          shareLocation={shareLocation}
          setShareLocation={setShareLocation}
          isReporting={isReporting}
          otherRole={userRole === 'Driver' ? 'Passenger' : 'Driver'}
          t={t}
        />
      )}

      {/* Provide Proof Dialog - FIXED */}
      {showProofDialog && (
        <ProvideProofDialog
          onCancel={() => setShowProofDialog(false)}
          onConfirm={handleProvideProof}
          isConfirming={isConfirming}
          t={t}
        />
      )}
    </div>
  );
};

// Safety Check Dialog Component
const SafetyCheckDialog = ({
  onCancel,
  onConfirm,
  isConfirming,
  safetyChecked,
  setSafetyChecked,
  t
}) => {

  return ReactDOM.createPortal(
    <div 
      className="RAC-modal-overlay-v2" 
      onClick={(e) => {
        if (e.target === e.currentTarget && !isConfirming) {
          onCancel();
        }
      }}
    >
      <div className="RAC-modal2-v2">
        <div className="RAC-safety-dialog">
          <div className="RAC-dialog-content">
            <div className="RAC-safety-icon">
              <FaExclamationTriangle size={32} color="#F59E0B" />
            </div>
            <h4>{t('ride_actions.safety_check')}</h4>
            <p>{t('ride_actions.before_starting_journey')}</p>
            
            <div className="RAC-safety-checklist">
              <label className="RAC-safety-item">
                <input
                  type="checkbox"
                  checked={safetyChecked}
                  onChange={(e) => setSafetyChecked(e.target.checked)}
                  disabled={isConfirming}
                />
                <span>{t('ride_actions.both_users_ready_vehicle')}</span>
              </label>
            </div>
            
            <div className="RAC-safety-info">
              <p><strong>{t('ride_actions.important')}:</strong></p>
              <ul>
                <li>{t('ride_actions.ensure_passengers_seated')}</li>
                <li>{t('ride_actions.vehicle_good_condition')}</li>
                <li>{t('ride_actions.route_planned_agreed')}</li>
                <li>{t('ride_actions.emergency_contacts_accessible')}</li>
              </ul>
            </div>
            
            <div className="RAC-dialog-actions">
              <CustomButton10Ca
                onClick={onCancel}
                label={t('buttons.cancel')}
                disabled={isConfirming}
              />
              <CustomButton10Co
                onClick={onConfirm}
                disabled={isConfirming || !safetyChecked}
              >
                {isConfirming ? <Spinner size="small" showText={false} /> : t('ride_actions.start_journey')}
              </CustomButton10Co>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

// Other Not Here Dialog Component
const OtherNotHereDialog = ({
  onCancel,
  onConfirm,
  shareLocation,
  setShareLocation,
  isReporting,
  otherRole,
  t
}) => {
  return ReactDOM.createPortal(
    <div 
      className="RAC-modal-overlay-v2" 
      onClick={(e) => {
        if (e.target === e.currentTarget && !isReporting) {
          onCancel();
        }
      }}
    >
      <div className="RAC-modal2-v2">
        <div className="RAC-other-not-here-dialog">
          <div className="RAC-dialog-content">
            <div className="RAC-warning-icon">
              <FaExclamationTriangle size={32} color="#F59E0B" />
            </div>
            <h4>{t('ride_actions.report_not_present_title', { role: otherRole })}</h4>
            <p>{t('ride_actions.report_not_present_description')}</p>
            <p><strong>{t('ride_actions.they_have_10_minutes_respond')}</strong></p>
            
            <div className="RAC-location-option">
              <label className="RAC-checkbox-label">
                <input
                  type="checkbox"
                  checked={shareLocation}
                  onChange={(e) => setShareLocation(e.target.checked)}
                  disabled={isReporting}
                />
                {t('ride_actions.share_location_as_proof')}
              </label>
              <p className="RAC-location-help">
                {t('ride_actions.location_evidence_description')}
              </p>
            </div>
            
            <div className="RAC-dialog-actions">
              <CustomButton10Ca
                onClick={onCancel}
                label={t('buttons.go_back')}
                disabled={isReporting}
              />
              <CustomButton10Co
                onClick={onConfirm}
                disabled={isReporting}
              >
                {isReporting ? <Spinner size="small" showText={false} /> : t('ride_actions.report_not_present')}
              </CustomButton10Co>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

// Provide Proof Dialog Component - FIXED
const ProvideProofDialog = ({
  onCancel,
  onConfirm,
  isConfirming,
  t
}) => {
  return ReactDOM.createPortal(
    <div 
      className="RAC-modal-overlay-v2" 
      onClick={(e) => {
        if (e.target === e.currentTarget && !isConfirming) {
          onCancel();
        }
      }}
    >
      <div className="RAC-modal2-v2">
        <div className="RAC-provide-proof-dialog">
          <div className="RAC-dialog-content">
            <div className="RAC-location-icon">
              <FaMapMarkerAlt size={32} color="#3B82F6" />
            </div>
            <h4>{t('ride_actions.share_location_proof_title')}</h4>
            <p>{t('ride_actions.share_location_proof_description')}</p>
            
            <div className="RAC-proof-info">
              <p><strong>{t('ride_actions.note')}:</strong> {t('ride_actions.gps_location_recorded')}</p>
            </div>
            
            <div className="RAC-dialog-actions">
              <CustomButton10Ca
                onClick={onCancel}
                label="Cancel"
                disabled={isConfirming}
              />
              <CustomButton10Co
                onClick={onConfirm}
                disabled={isConfirming}
              >
                {isConfirming ? <Spinner size="small" showText={false} /> : t('ride_actions.share_location_proof')}
              </CustomButton10Co>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ReadyConfirmation;